import { UserRight } from "@/assets";
import { Button } from "@/components/ui/button";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useAuthState } from "@/context";
import { ToastMessage, type } from "@/lib/toastr";
import {
  complete,
  markATransaction,
  refreshTransaction,
  requeueTransaction,
} from "@/services/Transactions.service";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlignVerticalSpaceAround,
  BookText,
  CheckSquare,
  ChevronDown,
  RefreshCcw,
  SquareCheck,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import ChangeBeneficiary from "./ChangeBeneficiary";
import { Storage } from "@/services/storage-services";
import UpdateTransaction from "./UpdateTransaction";

const TransactionActions = ({
  id,
  status,
  normalStatus,
  customerId,
}: {
  id: string;
  customerId: string;
  status: string;
  normalStatus: string;
}) => {
  const { state } = useAuthState();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const mutationRequeue = useMutation({
    mutationFn: async () => await requeueTransaction(id),
    mutationKey: ["requeue"],

    onSuccess: () => {
      queryClient.invalidateQueries("transactions");
      ToastMessage(type.SUCCESS, "Transaction has been queued successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data?.message);
    },
  });

  const mutationRefresh = useMutation({
    mutationFn: async (payload) => await refreshTransaction(id, payload),
    mutationKey: ["refresh"],

    onSuccess: () => {
      queryClient.invalidateQueries("transactions");
      ToastMessage(type.SUCCESS, "Transaction has been refresh successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data?.message);
    },
  });
  const mutationMarkAs = useMutation({
    mutationFn: async (payload) => await markATransaction(id, payload),
    mutationKey: ["markAs"],

    onSuccess: () => {
      queryClient.invalidateQueries("transactions");
      ToastMessage(
        type.SUCCESS,
        "Transaction has been marked as success successfully"
      );
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data?.message);
    },
  });
  const mutationComplete = useMutation({
    mutationFn: async () => await complete(id),
    mutationKey: ["complete"],

    onSuccess: () => {
      queryClient.invalidateQueries("transactions");
      ToastMessage(type.SUCCESS, "Transaction completed successfully");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data?.message);
    },
  });

  const merchant = Storage.getItem("selectedMerchant");


  return (
    <>
      {location?.pathname !== "/dashboard/transactions" ? (
        <>
          <ChangeBeneficiary
            id={customerId}
            transactionId={id}
            open={isOpen}
            onOpenChange={setIsOpen}
            size="lg"
          />

          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              disabled={
                status === "success" ||
                normalStatus === "success" ||
                normalStatus === "cancelled" ||
                status === "queued"
              }
            >
              <Button
                disabled={
                  status === "success" ||
                  normalStatus === "success" ||
                  normalStatus === "cancelled" ||
                  status === "queued"
                }
              >
                Quick Action
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">

              {merchant?.roleId === "merchant-admin" ||
                merchant?.roleId === "merchant-owner" ? (
                <>
                  {(status === "blocked" ||
                    status === "pending_withdrawal" ||
                    status === "pending_swap" ||
                    status === "pending_usd_swap" ||
                    status === "pending_papyin") && (
                      <DropdownMenuItem
                        className="flex items-center gap-2 text-xs text-primary-9"
                        onClick={() => mutationComplete.mutate()}
                      >
                        <SquareCheck />
                        Complete Transaction
                      </DropdownMenuItem>
                    )}
                </>
              ) : null}
              {status === "failed" && (
                <>
                  <DropdownMenuItem
                    className="flex items-center gap-2 text-xs text-primary-9"
                    onClick={() => setIsOpen(true)}
                  >
                    <UserRight />
                    Change beneficiary
                  </DropdownMenuItem>
                </>
              )}

              {status === "failed" && (
                <>
                  <DropdownMenuItem
                    className="flex items-center gap-2 text-xs text-primary-9"
                    onClick={async () => {
                      await mutationRequeue.mutate();
                    }}
                  >
                    <AlignVerticalSpaceAround />
                    Queue Transaction
                  </DropdownMenuItem>
                </>
              )}
              {status === "processing" && (
                <DropdownMenuItem
                  className="flex items-center gap-2 text-xs text-primary-9"
                  onClick={async () => {
                    await mutationRefresh.mutate({
                      status: "success",
                    });
                  }}
                >
                  <RefreshCcw />
                  Refresh Transaction
                </DropdownMenuItem>
              )}
              {(status === "blocked" || status === "processing") && (
                <DropdownMenuItem
                  className="flex items-center gap-2 text-xs text-primary-9"
                  onClick={async () => {
                    await mutationMarkAs.mutate({
                      status: "success",
                    });
                  }}
                >
                  <CheckSquare />
                  Mark as successful
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : (
        <>
          {merchant?.roleId === "merchant-admin" ||
            merchant?.roleId === "merchant-owner" ? (
            <>
              {status === "blocked" ||
                status === "pending_withdrawal" ||
                status === "pending_swap" ||
                status === "pending_payin" ? (
                <DropdownMenuItem
                  className="flex items-center gap-2 text-xs text-primary-9"
                  onClick={() => mutationComplete.mutate()}
                >
                  <SquareCheck />
                  Complete Transaction
                </DropdownMenuItem>
              ) : null}
            </>
          ) : null}
          {status === "failed" && (
            <>
              <DropdownMenuItem
                className="flex items-center gap-2 text-xs text-primary-9"
                onClick={async () => {
                  await mutationRequeue.mutate();
                }}
              >
                <AlignVerticalSpaceAround />
                Queue Transaction
              </DropdownMenuItem>
            </>
          )}
          {status === "processing" && (
            <DropdownMenuItem
              className="flex items-center gap-2 text-xs text-primary-9"
              onClick={async () => {
                await mutationRefresh.mutate({
                  status: "success",
                });
              }}
            >
              <RefreshCcw />
              Refresh Transaction
            </DropdownMenuItem>
          )}
          {(status === "blocked" || status === "processing") && (
            <DropdownMenuItem
              className="flex items-center gap-2 text-xs text-primary-9"
              onClick={async () => {
                await mutationMarkAs.mutate({
                  status: "success",
                });
              }}
            >
              <CheckSquare />
              Mark as successful
            </DropdownMenuItem>
          )}
        </>
      )
      }
    </>
  );
};

export default TransactionActions;
